/**
 * ASSISTANT DE CLÔTURE — LE BANDEAU DE LA NUIT.
 *
 * Une clôture se fait pour UNE soirée précise : son visuel en fond la rend
 * reconnaissable au premier coup d'œil, là où l'ancien en-tête n'affichait
 * qu'une date et une icône de pièces. Dessous, les quatre chiffres qu'on vient
 * chercher : ce que les rapports annoncent, ce que le terrain a compté,
 * l'écart entre les deux, et les entrées.
 *
 * ═══ UN BANDEAU, DEUX ÉCRANS ═══
 * « Le même design quand on sélectionne une soirée, juste avant de cliquer
 * sur clôture » (l'exploitant, 07/10/2026) : la fiche de la nuit porte le même
 * bandeau. Le dessin vit donc dans `BandeauSoiree` — visuel, repli, identité,
 * actions, chiffres —, et chaque écran ne fournit que son CONTENU : la fiche
 * ses actions (ignorer, commencer ou reprendre) et le total de la nuit,
 * l'assistant les siens. `ClosingHero` garde exactement son nom et ses props.
 *
 * ═══ LE VISUEL NE VIENT JAMAIS DE L'EXTÉRIEUR ═══
 * La CSP n'autorise que les images de Gestion. Le worker a téléchargé et
 * préparé le visuel (net, et un fond déjà flouté) ; Gestion le sert. L'écran
 * demande d'abord s'il existe (`…/cover/meta`) : il ne sonde jamais une image
 * absente, et une adresse qui ne serait pas celle de Gestion est ignorée.
 *
 * ═══ SANS VISUEL, UN REPLI QUI N'A PAS L'AIR D'UN MANQUE ═══
 * Les halos d'ambiance du design system et l'icône de l'écran. Aucun cadre
 * vide, aucune image cassée.
 *
 * ═══ HONNÊTETÉ DES CHIFFRES ═══
 * Un montant inconnu s'écrit « — », jamais 0. L'écart n'est calculé que si
 * l'annoncé ET le compté existent. Les entrées viennent du serveur (billets
 * scannés + guestlist saisie) ; absentes, elles se disent absentes.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { CalendarDays, Coins, type LucideIcon } from 'lucide-react'
import { Link } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { formatEcart, formatMoney, tonSeuil } from './money.js'
import { useSeuilsFinance } from './FeuilleDeCaisse.js'
import {
  CLOSING_STATUS_LABELS, CLOSING_STATUS_PILL, ilYA, lireJson, nuitLongue,
  type ClosingDetail,
} from './ClosingCommon.js'
import { IgnorerCloture } from './NightValidation.js'
import { aideCompte, aideEcart, aideRapport, feuilleDeCaisse } from './night-sheet-model.js'

/* ═══════════════════════ le visuel de la soirée ═══════════════════════ */

export interface VisuelSoiree {
  cover_url: string
  backdrop_url: string
  width: number | null
  height: number | null
  dominant_hex: string | null
}

/** Seules les adresses de Gestion entrent dans la page. */
function interne(u: unknown): u is string {
  return typeof u === 'string' && u.startsWith('/api/v1/soirees/') && !u.startsWith('//')
}

function commeVisuel(v: unknown): { cover: VisuelSoiree | null } | null {
  const o = typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null
  if (!o || !('cover' in o)) return null
  const c = typeof o['cover'] === 'object' && o['cover'] !== null ? (o['cover'] as Record<string, unknown>) : null
  if (!c || !interne(c['cover_url']) || !interne(c['backdrop_url'])) return { cover: null }
  const entier = (x: unknown): number | null => (typeof x === 'number' && Number.isFinite(x) && x > 0 ? x : null)
  const hex = typeof c['dominant_hex'] === 'string' && /^#[0-9a-f]{6}$/i.test(c['dominant_hex']) ? c['dominant_hex'] : null
  return {
    cover: {
      cover_url: c['cover_url'], backdrop_url: c['backdrop_url'],
      width: entier(c['width']), height: entier(c['height']), dominant_hex: hex,
    },
  }
}

/*
 * Les visuels déjà lus, par soirée, le temps de la session. L'assistant et la
 * fiche de la nuit montrent le même : la fiche ouverte juste après une
 * finalisation monte d'emblée avec l'affiche, au lieu de partir de l'icône de
 * repli et de pousser la pastille d'état de côté quand l'affiche arrive (le
 * sceau se pose sur cette pastille). Le visuel est tout de même relu.
 */
const cache = new Map<string, VisuelSoiree | null>()

/** Le visuel déjà lu pour cette soirée ; `null` s'il n'y en a pas, ou s'il n'a pas encore été lu. */
export function visuelEnCache(soireeId: string): VisuelSoiree | null {
  return cache.get(soireeId) ?? null
}

/**
 * Le visuel de la soirée, ou `null` — sans visuel, sans droit, ou tant que la
 * réponse n'est pas arrivée. Les trois se rendent de la même façon : le repli.
 */
export function useVisuelSoiree(soireeId: string | null): VisuelSoiree | null {
  const [visuel, setVisuel] = useState(() => cache.get(soireeId ?? '') ?? null)
  useEffect(() => {
    setVisuel(soireeId ? cache.get(soireeId) ?? null : null)
    if (!soireeId) return
    let vivant = true
    void lireJson(`/api/v1/soirees/${encodeURIComponent(soireeId)}/cover/meta`, commeVisuel).then((r) => {
      if (r.etat !== 'ok') return
      cache.set(soireeId, r.valeur.cover)
      if (vivant) setVisuel(r.valeur.cover)
    })
    return () => { vivant = false }
  }, [soireeId])
  return visuel
}

/* ═══════════════════════ les chiffres clés ═══════════════════════ */

export function Chiffre(props: { libelle: string; valeur: string; ton?: string | undefined; aide: string }) {
  return (
    <div className="fwz-chiffre">
      <dt className="fwz-chiffre__libelle">{props.libelle}</dt>
      <dd className={`fwz-chiffre__valeur${props.ton ? ` ${props.ton}` : ''}`}>{props.valeur}</dd>
      <dd className="fwz-chiffre__aide">{props.aide}</dd>
    </div>
  )
}

function entier(n: number): string { return n.toLocaleString('fr-FR') }

/* ═══════════════════════ le bandeau, partagé ═══════════════════════ */

/**
 * LE BANDEAU D'UNE SOIRÉE — le dessin seul, sans règle métier.
 *
 * Visuel flouté et assombri en fond, vignette nette, surtitre, nom de la
 * soirée, pastilles d'état, actions à droite, chiffres clés dessous. Les
 * classes sont celles de l'assistant (`fwz-hero*`, finance-wizard.css) : un
 * seul dessin, que la fiche de la nuit reprend tel quel — et la fiche d'une
 * soirée de Billetterie Live Club aussi (« comme l'affichage dans
 * comptabilité », l'exploitant, 23/10/2026), qui fournit alors son affiche
 * (`visuel`) au lieu de la lire chez Shotgun.
 */
export function BandeauSoiree(props: {
  /** La soirée dont on montre le visuel ; `null` = le repli (halos et icône). */
  soiree: { id: string; name: string } | null
  /**
   * Le visuel, quand l'écran l'a déjà (l'affiche de Billetterie Live Club) :
   * il n'est alors pas lu chez Gestion (`…/cover/meta`). `null` = le repli.
   * Absent : le visuel Shotgun de `soiree`.
   */
  visuel?: VisuelSoiree | null
  /** Où mène « Retour » sans historique ; absent, le parent de l'adresse. */
  retour?: string
  surtitre: string
  titre: string
  /** Les pastilles d'état. */
  etat: ReactNode
  /** Une ligne de contexte sous les pastilles (qui, quand). */
  meta?: ReactNode
  /** Les gestes, à droite du retour. */
  actions: ReactNode
  /** Les chiffres clés : des `<Chiffre>`. */
  chiffres: ReactNode
  /** Une classe de plus sur la grille des chiffres (la fiche de nuit en compte un nombre variable). */
  chiffresClasse?: string
  /** L'icône du repli sans visuel. */
  icone?: LucideIcon
}) {
  const lu = useVisuelSoiree(props.visuel === undefined ? props.soiree?.id ?? null : null)
  const annonceVisuel = props.visuel === undefined ? lu : props.visuel
  /* Un fichier annoncé mais introuvable (volume restauré, purge) : on retombe
     sur le repli plutôt que de laisser un cadre vide. */
  const [enEchec, setEnEchec] = useState<string | null>(null)
  const visuel = annonceVisuel && enEchec !== annonceVisuel.cover_url ? annonceVisuel : null
  /* « Chargé » est mémorisé PAR ADRESSE : une image en cache peut finir de
     charger avant qu'un effet de remise à zéro ne passe, et resterait alors
     invisible. */
  const [fondCharge, setFondCharge] = useState<string | null>(null)
  const [afficheChargee, setAfficheChargee] = useState<string | null>(null)
  const fondPret = visuel !== null && fondCharge === visuel.backdrop_url
  const affichePrete = visuel !== null && afficheChargee === visuel.cover_url
  const Icone = props.icone ?? Coins

  const teinte = visuel?.dominant_hex ?? null
  const style = teinte ? ({ ['--fwz-teinte' as string]: teinte } as CSSProperties) : undefined

  return (
    <header className={`fwz-hero${visuel ? ' fwz-hero--visuel' : ' fwz-hero--sans-visuel'}`} style={style}>
      {visuel ? (
        <div className={`fwz-hero__fond${fondPret ? ' fwz-hero__fond--pret' : ''}`} aria-hidden="true">
          <img
            src={visuel.backdrop_url} alt="" decoding="async"
            onLoad={() => setFondCharge(visuel.backdrop_url)}
            onError={() => setEnEchec(visuel.cover_url)}
          />
        </div>
      ) : null}

      <div className="fwz-hero__barre">
        {props.retour ? <SmartBack fallback={props.retour} /> : <SmartBack />}
        <div className="fwz-hero__actions">{props.actions}</div>
      </div>

      <div className="fwz-hero__identite">
        {visuel ? (
          <figure
            className={`fwz-hero__affiche${affichePrete ? ' fwz-hero__affiche--prete' : ''}`}
            style={visuel.width && visuel.height ? { aspectRatio: `${visuel.width} / ${visuel.height}` } : undefined}
          >
            <img
              src={visuel.cover_url}
              alt={`Visuel de la soirée ${props.soiree?.name ?? ''}`.trim()}
              decoding="async"
              onLoad={() => setAfficheChargee(visuel.cover_url)}
              onError={() => setEnEchec(visuel.cover_url)}
            />
          </figure>
        ) : (
          <span className="fwz-hero__icone" aria-hidden="true"><Icone size={30} /></span>
        )}

        <div className="fwz-hero__textes">
          <p className="fwz-hero__surtitre">{props.surtitre}</p>
          <h1 className="fwz-hero__titre" tabIndex={-1}>{props.titre}</h1>
          <p className="fwz-hero__etat">{props.etat}</p>
          {props.meta ? <p className="fwz-hero__meta">{props.meta}</p> : null}
        </div>
      </div>

      <dl className={`fwz-chiffres${props.chiffresClasse ? ` ${props.chiffresClasse}` : ''}`}>{props.chiffres}</dl>
    </header>
  )
}

/* ═══════════════════════ le bandeau de l'assistant ═══════════════════════ */

export function ClosingHero(props: {
  closing: ClosingDetail
  /** Après « ignorer la clôture » (ou son annulation) : relire la clôture. */
  onIgnoree: () => void
}) {
  const d = props.closing
  const maintenues = d.soirees.filter((s) => s.status === 'SCHEDULED')
  const affichees = maintenues.length > 0 ? maintenues : d.soirees
  const premiere = maintenues[0] ?? null

  const titre = affichees.length > 0 ? affichees.map((s) => s.name).join(' · ') : nuitLongue(d.business_date)
  const annulee = maintenues.length === 0 && d.soirees.length > 0

  /* ─── les quatre chiffres ─── */
  // Les totaux de la FEUILLE DE CAISSE (night-sheet-model), ceux que la
  // synthèse affiche : hors offerts, et l'écart lu au point de vente — compté
  // en entier contre son rapport, comme le classeur. La règle ligne à ligne
  // (carte contre carte) donnait − 801 € sur la nuit du 02/10 là où le
  // classeur dit + 114 € : une salle sans ligne « Espèces » au rapport, dont
  // les 915 € comptés en coupures disparaissaient (recette du 07/10/2026).
  //
  // Les AIDES aussi viennent du modèle (`aideCompte`, `aideEcart`,
  // `aideRapport`), partagées avec la fiche de nuit : « tout est compté »
  // n'oublie plus un point de vente sans réponse, ni des espèces comptées
  // qu'aucune ligne ne porte encore (revue du 07/10/2026).
  const f = feuilleDeCaisse(d, null)
  const seuils = useSeuilsFinance()
  const annonce = f.total.rapport
  const compte = f.total.compte
  const ecart = f.total.ecart
  const e = formatEcart(ecart)
  const ecartsOuverts = d.discrepancies.filter((x) => x.status === 'OUVERT').length
  const entrees = d.entries ?? null

  return (
    <BandeauSoiree
      soiree={premiere}
      surtitre={`Clôture · ${nuitLongue(d.business_date)}`}
      titre={titre}
      etat={(
        <>
          <span className={CLOSING_STATUS_PILL[d.status]}>{CLOSING_STATUS_LABELS[d.status]}</span>
          {d.revision > 1 ? <span className="lc-pill lc-pill--idle">Révision {d.revision}</span> : null}
          {annulee ? <span className="lc-pill lc-pill--idle">Soirée annulée</span> : null}
        </>
      )}
      meta={(
        <>
          <span>Ouverte {ilYA(d.opened_at)}{d.opened_by_name ? ` par ${d.opened_by_name}` : ''}</span>
          {d.locked_by_name ? (
            <span>
              Dernière écriture {d.locked_is_me ? 'par vous' : `par ${d.locked_by_name}`}
              {d.locked_at ? `, ${ilYA(d.locked_at)}` : ''}
            </span>
          ) : null}
        </>
      )}
      actions={(
        <>
          <Link className="lc-btn lc-btn--ghost lc-btn--sm" href={`/comptabilite/nuits/${d.business_date}`}>
            <CalendarDays size={15} aria-hidden="true" />Fiche de la nuit
          </Link>
          {d.status !== 'SUPERSEDED' ? <IgnorerCloture date={d.business_date} onDone={props.onIgnoree} compact /> : null}
        </>
      )}
      chiffres={(
        <>
          <Chiffre
            libelle="Annoncé"
            valeur={formatMoney(annonce)}
            aide={aideRapport(f)}
          />
          <Chiffre
            libelle="Compté"
            valeur={formatMoney(compte)}
            aide={aideCompte(f)}
          />
          <Chiffre
            libelle="Écart"
            valeur={e.texte}
            ton={ecart === null ? undefined : `fi-seuil--${tonSeuil(ecart, seuils)}`}
            aide={ecart !== null && ecartsOuverts > 0
              ? `${ecartsOuverts} écart${ecartsOuverts > 1 ? 's' : ''} encore à expliquer`
              : aideEcart(f)}
          />
          <Chiffre
            libelle="Entrées"
            valeur={entrees ? entier(entrees.total) : '—'}
            aide={entrees
              ? `${entier(entrees.shotgun_scanned)} billets scannés · guestlist ${entrees.guestlist_count === null ? 'à saisir' : entier(entrees.guestlist_count)}`
              : 'Pas encore relevées pour cette nuit'}
          />
        </>
      )}
    />
  )
}
